// Append bounded server-rendered pages; filtering and switching views cancel stale loads.
// "1-12 of 27 documents", "1 of 1 document"; the bare form ("0 documents") has no range.
const COUNT_LABEL = /(\d+)(?:-(\d+))? of (\d+) documents?/

export class DocumentLibraryPaging {
  constructor(root) { this.root = root }

  refresh() {
    this.stop()
    this.more = this.root.querySelector('.document-library-load-more')
    this.next = this.root.querySelector('.document-library-pagination .next a')?.href
    if (!this.more) return
    this.more.hidden = this.root.dataset.view !== 'cards' || !this.next
    this.more.querySelector('p').textContent = ''
    this.more.querySelector('button').disabled = false
    if (!this.more.hidden) this.watch()
  }

  watch() {
    if (!this.next || this.root.dataset.view !== 'cards') return
    this.observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) this.load()
    }, { rootMargin: '300px' })
    this.observer.observe(this.more)
  }

  async load() {
    if (!this.next || this.request || this.root.dataset.view !== 'cards') return
    this.observer?.disconnect()
    const request = this.request = new AbortController()
    const button = this.more.querySelector('button'), status = this.more.querySelector('p')
    button.disabled = true;status.textContent = 'Loading more documents…'
    try {
      const page = await this.fetchPage(request)
      if (this.request !== request) return
      this.append(page)
      status.textContent = this.next ? '' : 'All documents loaded'
      button.hidden = !this.next;button.textContent = 'Load more documents'
      this.watch()
    } catch (error) {
      if (error.name !== 'AbortError') { status.textContent = 'More documents could not be loaded.';button.textContent = 'Try again' }
    } finally {
      if (this.request === request) { this.request = null;button.disabled = false }
    }
  }

  async fetchPage(request) {
    const url = new URL(this.next)
    url.searchParams.set('library_page', '1')
    url.searchParams.set('library_view', 'cards')
    const response = await fetch(url, { credentials: 'same-origin', signal: request.signal })
    if (!response.ok || response.redirected) throw Error('Page unavailable')
    const page = new DOMParser().parseFromString(await response.text(), 'text/html')
    if (!page.querySelector('#document-library-items')) throw Error('Invalid page')
    return page
  }

  append(page) {
    const items = this.root.querySelector('#document-library-items')
    const existing = new Set([...items.children].map(item => item.id))
    items.append(...[...page.querySelector('#document-library-items').children].filter(item => !existing.has(item.id)))
    const old = this.root.querySelector('.document-library-pagination'), next = page.querySelector('.document-library-pagination')
    if (next) old.replaceWith(next)
    else old?.remove()
    this.next = next?.querySelector('.next a')?.href
    // DOMParser resolves relative URLs against about:blank; retain the live origin.
    if (next?.querySelector('.next a')) this.next = new URL(next.querySelector('.next a').getAttribute('href'), location.href).href
    this.syncCount(items)
  }

  // The count line is rendered by the server for the page that was loaded, so it
  // already carries the filtered total and the "· Pinned" suffix. Appended pages keep
  // the first loaded page's start index, so the range is re-derived from the records
  // now on screen; only the label text is replaced, never the suffix.
  syncCount(items) {
    const count = this.root.querySelector('.document-library-count')
    const node = count && [...count.childNodes].find(child => child.nodeType === Node.TEXT_NODE && COUNT_LABEL.test(child.textContent))
    const rendered = items.children.length
    if (!node || rendered === 0) return
    const [, first, , total] = node.textContent.match(COUNT_LABEL).map(value => Number(value))
    const last = Math.min(first + rendered - 1, total)
    const range = first === last ? `${first}` : `${first}-${last}`
    node.textContent = node.textContent.replace(COUNT_LABEL, `${range} of ${total} ${total === 1 ? 'document' : 'documents'}`)
  }

  stop() {
    this.observer?.disconnect()
    this.request?.abort()
    this.request = null
    this.next = null
  }
}
